import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Chip,
  Tooltip,
  CircularProgress,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  FormHelperText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
} from "@mui/icons-material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import useEffectiveRolePath, { normalizeRoleSlug } from "../../../hooks/useEffectiveRolePath";
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  pointerWithin,
  closestCorners,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useTimetableById, useCreateSlot, useUpdateSlot, useDeleteSlot, useValidateSlot, useUpdateTimetableMeta } from "../../../hooks/queries/useTimetable";
import { useAllDepartments } from "../../../hooks/queries/useDepartments";
import { usePermissions } from "../../../hooks/usePermissions";
import timetableService from "../../../services/timetableService";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { useClasses } from "../../../hooks/queries/useCourses";
import { useSubjectsByClass } from "../../../hooks/queries/useClassSubject";
import { useTeachersBySubject } from "../../../hooks/queries/useTeacherSubject";
import { toast } from "react-toastify";
import {
  toTimeInputValue,
  normalizeSlotFk,
  findBreakConflictForTeachingSlot,
  augmentScheduleRowsWithSlots,
  slotOverlapsScheduleCell,
  slotStartsInScheduleCell,
  isSubjectTimetableCellBlocked,
  isTeacherTimetableCellBlocked,
} from "../../../utils/timetableTimeSlots";
import { getTimetableSlotOccurrenceStatus, timetableSlotOccurrenceLabel } from "../../../utils/timetableSlotTiming";
import {
  getScheduleSlotsPerDayFromDetail,
  getDefaultSchoolDayScheduleRows,
} from "../../../utils/timetableScheduleFromDetail";

const DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/** Droppable ids are `${day}_${HH:mm}` — parse without splitting on every underscore in future labels. */
function parseCellDroppableId(id) {
  const s = String(id ?? "");
  const i = s.indexOf("_");
  if (i <= 0 || i >= s.length - 1) return null;
  return { day: s.slice(0, i), startTime: s.slice(i + 1) };
}

/**
 * closestCenter fails for table grids: the dragged card's center stays near the source cell.
 * Prefer pointerWithin so the cell under the cursor wins; fall back to closestCorners.
 */
function timetableKanbanCollisionDetection(args) {
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length > 0) {
    return pointerCollisions;
  }
  return closestCorners(args);
}

function slotOverlapsConfiguredBreak(slot, scheduleRows) {
  if (!slot || !Array.isArray(scheduleRows) || scheduleRows.length === 0) return false;
  return Boolean(findBreakConflictForTeachingSlot(scheduleRows, slot.startTime, slot.endTime));
}

/** Build a teacher row from slot payload when GET /subjects/:id/teachers omits this teacher (inactive assignment, etc.) */
function teacherFromEditingSlot(slot) {
  if (!slot) return null;
  const tid = slot.teacherId ?? slot.teacher?.id;
  if (tid == null || tid === "") return null;
  const t = slot.teacher;
  const user = t?.user ?? t?.User ?? null;
  return {
    id: tid,
    user: user || { fullName: null, firstName: null, lastName: null, email: null },
  };
}

function classFromEditingSlot(slot) {
  if (!slot) return null;
  const cid = slot.classId ?? slot.class?.id;
  if (cid == null || cid === "") return null;
  const cls = slot.class ?? {};
  return {
    id: cid,
    name: cls.name ?? cls.className ?? `Class ${cid}`,
    section: cls.section ?? cls.classSection ?? cls.sectionName ?? "",
    practicalBatches: cls.practicalBatches ?? cls.practical_batches ?? [],
  };
}

function subjectFromEditingSlot(slot) {
  if (!slot) return null;
  const sid = slot.subjectId ?? slot.subject?.id;
  if (sid == null || sid === "") return null;
  const sub = slot.subject ?? {};
  return {
    id: sid,
    name: sub.name ?? sub.subjectName ?? `Subject ${sid}`,
    code: sub.code ?? sub.subjectCode ?? "",
  };
}

function getTeacherId(t) {
  if (!t) return null;
  return t.id ?? t.teacherId ?? t.teacher?.id ?? t._id ?? null;
}

function getTeacherDisplayName(t) {
  if (!t) return "";
  return (
    t.user?.fullName ??
    t.User?.fullName ??
    t.fullName ??
    (t.user?.firstName || t.user?.lastName
      ? [t.user?.firstName, t.user?.lastName].filter(Boolean).join(" ")
      : t.User?.firstName || t.User?.lastName
        ? [t.User?.firstName, t.User?.lastName].filter(Boolean).join(" ")
        : [t.firstName, t.lastName].filter(Boolean).join(" ")) ??
    ""
  );
}

function formatClassLabel(cls) {
  const name = cls?.name ?? cls?.className ?? "";
  const section = cls?.section ?? cls?.classSection ?? cls?.sectionName ?? "";
  return section ? `${name} - ${section}` : name || "—";
}

function getSubjectLabelFromSlot(slot) {
  const sub = slot?.subject || {};
  const name = sub.name || sub.subjectName || "";
  const code = sub.code || sub.subjectCode || "";
  if (name && code) return `${name} (${code})`;
  return name || code || "";
}

function slotMatchesSubjectFilter(slot, filterParam) {
  const norm = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
  const normFilter = norm(filterParam);
  const subj = slot.subject || {};
  const name = subj.name || subj.subjectName || "";
  const label = getSubjectLabelFromSlot(slot);
  const filterId = Number(filterParam);
  const subjectId = subj.id ?? slot.subjectId ?? null;
  if (Number.isFinite(filterId) && String(filterId) === String(filterParam).trim()) {
    return String(subjectId) === String(filterId);
  }
  return norm(label) === normFilter || norm(name) === normFilter;
}

function kanbanModeLabel(mode) {
  if (mode === "teacher") return "Teacher";
  if (mode === "subject") return "Subject";
  return "Class";
}

function inferLectureTypeFromMode(mode) {
  const code = String(mode?.code || "").trim().toUpperCase();
  const name = String(mode?.name || "").trim().toUpperCase();
  if (code.includes("LAB") || name.includes("LAB")) return "LAB";
  if (code.includes("PRACTICAL") || name.includes("PRACTICAL")) return "PRACTICAL";
  if (code.includes("PROJECT") || name.includes("PROJECT")) return "PROJECT";
  if (code.includes("INTERNSHIP") || name.includes("INTERNSHIP")) return "INTERNSHIP";
  if (code.includes("TUTORIAL") || name.includes("TUTORIAL")) return "TUTORIAL";
  if (code.includes("ONLINE") || name.includes("ONLINE")) return "ONLINE";
  return "THEORY";
}

function extractPracticalBatchesFromClass(cls) {
  const raw =
    cls?.practicalBatches ??
    cls?.practicalBatches ??
    cls?.practical_batches ??
    cls?.dataValues?.practicalBatches ??
    cls?.dataValues?.practical_batches;

  let list = [];
  if (Array.isArray(raw)) {
    list = raw;
  } else if (typeof raw === "string") {
    list = raw.split(",");
  } else if (raw && typeof raw === "object") {
    list = Object.values(raw);
  }

  return Array.from(
    new Set(
      list
        .map((b) => String(b || "").trim().toUpperCase())
        .filter((b) => b && b !== "0")
    )
  );
}

function SlotCard({
  slot,
  onEdit,
  onDelete,
  isDragging,
  showBreakWarning,
  canEdit = true,
  canDelete = true,
}) {
  const occurrenceStatus = getTimetableSlotOccurrenceStatus(slot);
  const occurrenceLabel = timetableSlotOccurrenceLabel(occurrenceStatus);
  const { attributes, listeners, setNodeRef } = useDraggable({
    id: `slot-${slot.id}`,
    data: slot,
  });

  const teacherName = slot.teacher?.user?.fullName ?? slot.teacher?.User?.fullName ?? "—";
  const subjectName = slot.subject?.name || "—";
  const className = formatClassLabel(slot.class);
  const slotLectureType = String(slot.lectureType || (slot.practicalBatchCode ? "PRACTICAL" : "THEORY")).toUpperCase();

  return (
    <Card
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      sx={{
        cursor: "grab",
        opacity: isDragging ? 0.5 : 1,
        touchAction: "none",
        "&:active": { cursor: "grabbing" },
      }}
    >
      <CardContent sx={{ py: 1, "&:last-child": { pb: 1 } }}>
        {/* Show slot time above subject/teacher/class */}
        <Typography variant="caption" color="primary" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
          {slot.startTime && slot.endTime ? `${slot.startTime.slice(0,5)} - ${slot.endTime.slice(0,5)}` : ""}
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5, flexWrap: "wrap", gap: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {subjectName}
          </Typography>
          {occurrenceLabel && (
            <Chip
              size="small"
              label={occurrenceLabel}
              color={occurrenceStatus === "IN_PROGRESS" ? "info" : "default"}
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 0.5 }}>
          <Chip
            size="small"
            label={slotLectureType === "THEORY" ? "Theory" : slotLectureType}
            color={slotLectureType === "THEORY" ? "primary" : "secondary"}
            variant="outlined"
            sx={{ height: 20, fontSize: "0.65rem" }}
          />
          {slot.practicalBatchCode ? (
            <Chip size="small" label={`Batch ${slot.practicalBatchCode}`} color="secondary" variant="outlined" sx={{ height: 20, fontSize: "0.65rem" }} />
          ) : null}
          {slot.labNo ? (
            <Chip size="small" label={`Lab ${slot.labNo}`} sx={{ height: 20, fontSize: "0.65rem" }} />
          ) : null}
          {slotLectureType === "ONLINE" && slot.meetingLink ? (
            <Chip
              size="small"
              label="Link"
              component="a"
              href={slot.meetingLink}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              color="info"
              variant="outlined"
              sx={{ height: 20, fontSize: "0.65rem" }}
            />
          ) : null}
        </Box>
        <Typography variant="caption" color="text.secondary" display="block">
          {teacherName}
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block">
          {className} {slot.room ? `• ${slot.room}` : ""}
        </Typography>
        {showBreakWarning && (
          <Tooltip title="Time overlaps a scheduled break — move to a lecture period or edit times">
            <Chip size="small" color="warning" label="Break overlap" sx={{ mt: 0.5, height: 20, fontSize: "0.65rem" }} />
          </Tooltip>
        )}
        {(canEdit || canDelete) && (
          <Box sx={{ mt: 1, display: "flex", gap: 0.5, alignItems: "center" }}>
            {canEdit ? (
              <Tooltip title="Edit slot">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit(slot);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canDelete ? (
              <Tooltip title="Delete slot">
                <span>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.(slot);
                    }}
                  >
                    <Delete fontSize="small" color="error" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

function DroppableCell({ id, children }) {
  const { isOver, setNodeRef } = useDroppable({ id });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minHeight: 104,
        height: "100%",
        p: 0.75,
        borderRadius: 1,
        bgcolor: isOver ? "action.selected" : "background.paper",
        border: "1px dashed",
        borderColor: isOver ? "primary.main" : "divider",
        display: "flex",
        flexDirection: "column",
        justifyContent: "stretch",
        transition: "border-color 0.15s ease, background-color 0.15s ease",
        position: "relative",
        zIndex: isOver ? 1 : 0,
      }}
    >
      {children}
    </Box>
  );
}

const TimetableKanbanEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { rolePath } = useEffectiveRolePath();
  const { hasAnyPermission } = usePermissions();
  const canEditSlots = hasAnyPermission(["timetable.update", "timetable.create"]);
  const canDeleteSlots = hasAnyPermission(["timetable.delete", "timetable.update"]);

  // Department editing: only admin/superadmin may move a timetable across departments.
  // (HOD is locked to their own department by the backend.) Requires timetable.update.
  const authUser = useSelector((state) => state.auth?.user);
  const normalizedRole = normalizeRoleSlug(authUser?.role);
  const canManageDepartments = ["admin", "superadmin"].includes(normalizedRole);
  const canEditTimetableDetails = canEditSlots && canManageDepartments;

  // Edit-details dialog state (name + department).
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsForm, setDetailsForm] = useState({ name: "", departmentId: "" });
  const updateTimetableMetaMutation = useUpdateTimetableMeta();
  
  // Parse URL parameters for filtering
  const urlParams = new URLSearchParams(search);
  const filterParam = urlParams.get('filter');
  const modeParam = urlParams.get('mode'); // 'class' | 'teacher' | 'subject'
  
  const { data: timetableData, isLoading, isFetching, error } = useTimetableById(id);
  const {
    data: lectureModesRes,
    isLoading: lectureModesLoading,
    isError: lectureModesError,
    error: lectureModesErr,
  } = useQuery({
    queryKey: ["timetable", id, "lecture-modes"],
    queryFn: async () => {
      const res = await timetableService.getLectureModes(id);
      const body = res?.data;
      const raw = body?.data !== undefined ? body.data : body;
      if (Array.isArray(raw)) return raw;
      return [];
    },
    enabled: Boolean(id),
    staleTime: 60 * 1000,
  });
  const lectureModes = Array.isArray(lectureModesRes) ? lectureModesRes : [];
  const queryClient = useQueryClient();
  const timetable = timetableData;
  const createSlotMutation = useCreateSlot(id);
  const updateSlotMutation = useUpdateSlot(id);
  const deleteSlotMutation = useDeleteSlot(id);
  const validateSlotMutation = useValidateSlot(id);

  // Helper function to convert time to minutes
  const timeToMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return 0;
    const s = hhmm.trim().slice(0, 5);
    const [h, m] = s.split(":").map((x) => Number.parseInt(x, 10));
    if (Number.isNaN(h) || Number.isNaN(m)) return 0;
    return h * 60 + m;
  };

  const [slotForm, setSlotForm] = useState({
    classId: "",
    subjectId: "",
    teacherId: "",
    dayOfWeek: "",
    startTime: "",
    endTime: "",
    room: "",
    lectureType: "THEORY",
    meetingLink: "",
  });

  const instituteId = timetable?.instituteId;

  // Departments for the "Edit details" department selector (admin/superadmin only, and only
  // when the timetable's institute is known). Reuses the same hook as the generation wizard.
  const { data: departmentsData, isLoading: departmentsLoading } = useAllDepartments(
    {
      limit: 9999,
      page: 1,
      ...(instituteId && { instituteId }),
    },
    { enabled: canEditTimetableDetails && !!instituteId }
  );
  const departments = Array.isArray(departmentsData?.data)
    ? departmentsData.data
    : departmentsData?.data?.departments || [];

  const openEditDetails = () => {
    setDetailsForm({
      name: timetable?.name || "",
      departmentId:
        timetable?.departmentId != null ? String(timetable.departmentId) : "",
    });
    setDetailsDialogOpen(true);
  };

  const handleSaveDetails = async () => {
    const body = {};
    const trimmedName = String(detailsForm.name || "").trim();
    if (trimmedName && trimmedName !== timetable?.name) {
      body.name = trimmedName;
    }
    // departmentId: "" → institute-level (null); a value → that department.
    const nextDeptId =
      detailsForm.departmentId === "" ? null : Number(detailsForm.departmentId);
    const currentDeptId =
      timetable?.departmentId != null ? Number(timetable.departmentId) : null;
    if (Number(nextDeptId ?? -1) !== Number(currentDeptId ?? -1)) {
      body.departmentId = nextDeptId;
    }

    if (Object.keys(body).length === 0) {
      setDetailsDialogOpen(false);
      return;
    }

    try {
      await updateTimetableMetaMutation.mutateAsync({ id, body });
      toast.success("Timetable details updated");
      setDetailsDialogOpen(false);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Failed to update timetable details"
      );
    }
  };

  const { data: classesData } = useClasses({
    page: 1,
    limit: "all",
    sort: "name",
    order: "ASC",
    ...(instituteId && { instituteId }),
    // Scope the Add/Edit Slot dialog's Class dropdown to the timetable's own
    // department. When the timetable is institute-level (departmentId null),
    // fall through to the full institute class list (correct for a NULL-bucket
    // timetable). The filter object is recomputed each render from `timetable`,
    // so moving the timetable across departments via "Edit Details" re-scopes
    // this automatically once `timetable` is refetched.
    ...(timetable?.departmentId != null && {
      departmentId: timetable.departmentId,
    }),
  });
  const subjectsQueryParams = useMemo(
    () =>
      timetable?.academicYear
        ? { academicYear: timetable.academicYear }
        : {},
    [timetable?.academicYear]
  );
  const { data: classSubjectsData, isLoading: subjectsLoading } = useSubjectsByClass(
    slotForm.classId || null,
    subjectsQueryParams
  );
  const { data: subjectTeachersData, isLoading: teachersLoading } = useTeachersBySubject(
    slotForm.subjectId || null,
    slotForm.classId || null
  );

  const classes = Array.isArray(classesData?.data)
    ? classesData.data
    : (classesData?.data?.data || classesData?.data?.classes || classesData?.data?.rows || []);
  // IMPORTANT: keep only array shapes; if backend returns an object envelope,
  // our previous broad fallback could turn `subjects` into a non-array, causing empty dropdowns.
  const subjects =
    (Array.isArray(classSubjectsData?.data?.data) ? classSubjectsData.data.data : null) ??
    classSubjectsData?.data?.data?.subjects ??
    classSubjectsData?.data?.subjects ??
    [];

  const teachers =
    (Array.isArray(subjectTeachersData?.data?.data) ? subjectTeachersData.data.data : null) ??
    subjectTeachersData?.data?.teachers ??
    subjectTeachersData?.data?.data?.teachers ??
    [];

  // When editing a filtered class/teacher timetable, keep the slot form in sync with URL params.
  // This ensures the Subject API refetches when switching between classes.
  useEffect(() => {
    if (!filterParam || !modeParam) return;
    const norm = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
    const filterId = Number(filterParam);

    if (modeParam === "class") {
      // Prefer resolving from classes list; fallback to timetable slots (available immediately on this screen).
      const targetFromClasses = classes.find((c) => {
        const id = c.id ?? c.classId;
        const name = c.name || "";
        const section = c.section || c.classSection || "";
        const label = name ? (section ? `${name} - ${section}` : name) : "";
        if (Number.isFinite(filterId) && String(filterId) === String(filterParam).trim()) {
          return String(id) === String(filterId);
        }
        return norm(label) === norm(filterParam) || norm(name) === norm(filterParam);
      });

      const slotsNow = Array.isArray(timetable?.slots) ? timetable.slots : [];
      const targetFromSlots = slotsNow.find((s) => {
        const cls = s?.class || s?.Class || {};
        const id = cls?.id ?? s?.classId ?? s?.class_id ?? null;
        const name = cls?.name || "";
        const section = cls?.section || "";
        const label = name ? (section ? `${name} - ${section}` : name) : "";
        if (Number.isFinite(filterId) && String(filterId) === String(filterParam).trim()) {
          return String(id) === String(filterId);
        }
        return norm(label) === norm(filterParam) || norm(name) === norm(filterParam);
      });

      const nextClassId =
        (targetFromClasses ? String(targetFromClasses.id ?? targetFromClasses.classId) : "") ||
        (targetFromSlots ? String((targetFromSlots.class || targetFromSlots.Class || {})?.id ?? targetFromSlots.classId ?? targetFromSlots.class_id) : "") ||
        "";
      if (nextClassId && String(slotForm.classId || "") !== nextClassId) {
        setSlotForm((prev) => ({
          ...prev,
          classId: nextClassId,
          subjectId: "",
          teacherId: "",
        }));
      }
    } else if (modeParam === "subject") {
      const slotsNow = Array.isArray(timetable?.slots) ? timetable.slots : [];
      const targetFromSlots = slotsNow.find((s) => slotMatchesSubjectFilter(s, filterParam));
      const nextSubjectId = targetFromSlots
        ? String(targetFromSlots.subjectId ?? targetFromSlots.subject?.id ?? "")
        : "";
      if (nextSubjectId && String(slotForm.subjectId || "") !== nextSubjectId) {
        setSlotForm((prev) => ({
          ...prev,
          subjectId: nextSubjectId,
        }));
      }
    }
  }, [filterParam, modeParam, classes, timetable?.slots]); // intentionally not depending on slotForm to avoid loops

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSlot, setEditingSlot] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [activeSlot, setActiveSlot] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [isUpdatingSlot, setIsUpdatingSlot] = useState(false);
  const [isSavingSlot, setIsSavingSlot] = useState(false);
  const isSavingSlotRef = useRef(false);

  /** Default new slots to Theory (CLASS) once modes load */
  useEffect(() => {
    if (!dialogOpen || editingSlot || !lectureModes.length) return;
    setSlotForm((prev) => {
      if (prev.lectureModeId) return prev;
      const theory =
        lectureModes.find((m) => String(m.code || "").toUpperCase() === "CLASS") ||
        lectureModes[0];
      return { ...prev, lectureModeId: theory ? String(theory.id) : "" };
    });
  }, [dialogOpen, editingSlot, lectureModes]);

  /** Include current slot's lecture mode in the list so the Select can display it */
  const lectureModesForSelect = useMemo(() => {
    const list = Array.isArray(lectureModes) ? [...lectureModes] : [];
    if (!dialogOpen || !editingSlot) return list;
    const lm = editingSlot.lectureMode;
    const id = editingSlot.lectureModeId ?? lm?.id;
    if (!id) return list;
    if (list.some((m) => String(m.id) === String(id))) return list;
    if (lm?.id) {
      return [
        {
          id: lm.id,
          code: lm.code || "—",
          name: lm.name || "Current mode",
          requiresLabNo: lm.requiresLabNo,
        },
        ...list,
      ];
    }
    return list;
  }, [lectureModes, dialogOpen, editingSlot]);

  // MUI Select only shows a value if it exists in MenuItems. API list may omit the assigned teacher.
  const teachersForSelect = useMemo(() => {
    const list = (Array.isArray(teachers) ? teachers : [])
      .map((t) => ({
        ...t,
        id: getTeacherId(t),
      }))
      .filter((t) => t?.id != null);
    if (!dialogOpen) return list;
    const extra = teacherFromEditingSlot(editingSlot);
    if (!extra) return list;
    const exists = list.some((t) => String(t?.id) === String(extra.id));
    if (exists) return list;
    return [extra, ...list];
  }, [teachers, dialogOpen, editingSlot]);

  const classesForSelect = useMemo(() => {
    const list = Array.isArray(classes) ? [...classes] : [];
    if (!dialogOpen) return list;
    const extra = classFromEditingSlot(editingSlot);
    if (!extra) return list;
    const exists = list.some((c) => String(c?.id ?? c?.classId) === String(extra.id));
    if (exists) return list;
    return [extra, ...list];
  }, [classes, dialogOpen, editingSlot]);

  const classPracticalBatches = useMemo(() => {
    const selected = classesForSelect.find((c) => String(c?.id ?? c?.classId) === String(slotForm.classId || ""));
    const unique = extractPracticalBatchesFromClass(selected);
    if (slotForm.practicalBatchCode && !unique.includes(String(slotForm.practicalBatchCode).toUpperCase())) {
      unique.unshift(String(slotForm.practicalBatchCode).toUpperCase());
    }
    return unique;
  }, [classesForSelect, slotForm.classId, slotForm.practicalBatchCode]);

  const selectedLectureMode = useMemo(() => {
    return lectureModesForSelect.find((m) => String(m.id) === String(slotForm.lectureModeId || "")) || null;
  }, [lectureModesForSelect, slotForm.lectureModeId]);

  const effectiveLectureType = useMemo(() => {
    const explicit = String(slotForm.lectureType || "").toUpperCase();
    if (explicit) return explicit;
    return inferLectureTypeFromMode(selectedLectureMode);
  }, [slotForm.lectureType, selectedLectureMode]);

  const isBatchBasedLectureType = useMemo(() => {
    // THEORY and ONLINE are both whole-class (no batch required)
    return effectiveLectureType !== "THEORY" && effectiveLectureType !== "ONLINE";
  }, [effectiveLectureType]);

  useEffect(() => {
    if (!isBatchBasedLectureType && slotForm.practicalBatchCode) {
      setSlotForm((prev) => ({ ...prev, practicalBatchCode: "" }));
      return;
    }
    if (isBatchBasedLectureType && classPracticalBatches.length === 0 && slotForm.practicalBatchCode) {
      setSlotForm((prev) => ({ ...prev, practicalBatchCode: "" }));
    }
  }, [isBatchBasedLectureType, classPracticalBatches, slotForm.practicalBatchCode]);

  const subjectsForSelect = useMemo(() => {
    let list = Array.isArray(subjects) ? [...subjects] : [];
    if (!dialogOpen) return list;
    const extras = [];
    const extraFromEdit = subjectFromEditingSlot(editingSlot);
    if (extraFromEdit) extras.push(extraFromEdit);
    if (filterParam && modeParam === "subject") {
      const slotsNow = Array.isArray(timetable?.slots) ? timetable.slots : [];
      const match = slotsNow.find((s) => slotMatchesSubjectFilter(s, filterParam));
      const extraFromFilter = subjectFromEditingSlot(match);
      if (extraFromFilter) extras.push(extraFromFilter);
    }
    for (const extra of extras) {
      const exists = list.some((s) => String(s?.id ?? s?.subjectId) === String(extra.id));
      if (!exists) list = [extra, ...list];
    }
    return list;
  }, [subjects, dialogOpen, editingSlot, filterParam, modeParam, timetable?.slots]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 },
    })
  );

  // Same resolution as TimetableView: saved schedule + breaks, else default school day (not 24h hourly).
  const scheduleSlotsPerDay = useMemo(() => {
    const rows = getScheduleSlotsPerDayFromDetail(timetable);
    if (Array.isArray(rows) && rows.length > 0) return rows;
    return getDefaultSchoolDayScheduleRows();
  }, [timetable]);
  const allSlots = timetable?.slots || [];
  const timeSlots = useMemo(
    () => augmentScheduleRowsWithSlots(scheduleSlotsPerDay, allSlots),
    [scheduleSlotsPerDay, allSlots]
  );
  
  // Filter slots based on URL parameters
  const slots = useMemo(() => {
    if (!filterParam || !modeParam) return allSlots;
    
    const norm = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
    const normFilter = norm(filterParam);

    return allSlots.filter(slot => {
      if (modeParam === 'class') {
        const cls = slot.class || slot.Class || {};
        const classId = cls?.id ?? slot.classId ?? slot.class_id ?? null;
        const className = cls?.name || '';
        const section = cls?.section || '';
        const label = className ? (section ? `${className} - ${section}` : className) : '';
        const filterId = Number(filterParam);
        if (Number.isFinite(filterId) && String(filterId) === String(filterParam).trim()) {
          return String(classId) === String(filterId);
        }
        return norm(label) === normFilter || norm(className) === normFilter;
      } else if (modeParam === 'teacher') {
        const teacherName = getTeacherDisplayName(slot.teacher) || '';
        return norm(teacherName) === normFilter || teacherName === filterParam;
      } else if (modeParam === 'subject') {
        return slotMatchesSubjectFilter(slot, filterParam);
      }
      return true;
    });
  }, [allSlots, filterParam, modeParam]);

  const dialogBreakConflict = useMemo(
    () =>
      slotForm.startTime && slotForm.endTime
        ? findBreakConflictForTeachingSlot(scheduleSlotsPerDay, slotForm.startTime, slotForm.endTime)
        : null,
    [scheduleSlotsPerDay, slotForm.startTime, slotForm.endTime]
  );

  const getSlotsAt = useCallback(
    (day, cellStartTime) => {
      const dayUpper = String(day || "").toUpperCase();
      const cellRow = timeSlots.find(
        (ts) => toTimeInputValue(ts.start) === toTimeInputValue(cellStartTime)
      );
      if (!cellRow) return [];
      return slots.filter((s) => {
        if (String(s.dayOfWeek || "").toUpperCase() !== dayUpper) return false;
        return slotStartsInScheduleCell(s, cellRow.start, cellRow.end);
      });
    },
    [slots, timeSlots]
  );

  const isCellBlockedForAdd = useCallback(
    (day, cellStartTime) => {
      const dayUpper = String(day || "").toUpperCase();
      const cellRow = timeSlots.find(
        (ts) => toTimeInputValue(ts.start) === toTimeInputValue(cellStartTime)
      );
      if (!cellRow) return false;

      // Block if any visible slot overlaps this cell (e.g. a 2-hour lab that starts in the previous row)
      const hasOverlapping = slots.some((s) => {
        if (String(s.dayOfWeek || "").toUpperCase() !== dayUpper) return false;
        // Only block if the slot does NOT start in this cell (it would already be rendered via getSlotsAt)
        if (slotStartsInScheduleCell(s, cellRow.start, cellRow.end)) return false;
        return slotOverlapsScheduleCell(s, cellRow.start, cellRow.end);
      });
      if (hasOverlapping) return true;

      if (modeParam === "subject" && filterParam) {
        return isSubjectTimetableCellBlocked({
          allSlots,
          day,
          cellStart: cellRow.start,
          cellEnd: cellRow.end,
          subjectFilter: filterParam,
          subjectGroupSlots: slots,
          slotMatchesSubjectFilter,
        });
      }
      if (modeParam === "teacher" && filterParam) {
        return isTeacherTimetableCellBlocked({
          allSlots,
          day,
          cellStart: cellRow.start,
          cellEnd: cellRow.end,
          teacherFilter: filterParam,
          teacherGroupSlots: slots,
          getTeacherName: getTeacherDisplayName,
        });
      }
      return false;
    },
    [allSlots, slots, timeSlots, modeParam, filterParam]
  );

  // True when THIS group's own multi-period lecture (e.g. a 09:00–11:00 slot) continues into
  // this cell. Such a cell is a continuation of the lecture above, NOT a conflict — so the grid
  // must render it as "continued", never as a red "Unavailable".
  const isCellCoveredByOwnSpanningSlot = useCallback(
    (day, cellStartTime) => {
      const dayUpper = String(day || "").toUpperCase();
      const cellRow = timeSlots.find(
        (ts) => toTimeInputValue(ts.start) === toTimeInputValue(cellStartTime)
      );
      if (!cellRow) return false;
      return slots.some((s) => {
        if (String(s.dayOfWeek || "").toUpperCase() !== dayUpper) return false;
        // Starts in an earlier row but overlaps into this cell.
        if (slotStartsInScheduleCell(s, cellRow.start, cellRow.end)) return false;
        return slotOverlapsScheduleCell(s, cellRow.start, cellRow.end);
      });
    },
    [slots, timeSlots]
  );

  const unplacedSlots = useMemo(() => {
    const placed = new Set();
    for (const day of DAYS) {
      for (const ts of timeSlots) {
        for (const s of getSlotsAt(day, ts.start)) {
          if (s?.id != null) placed.add(Number(s.id));
        }
      }
    }
    return slots.filter((s) => s?.id != null && !placed.has(Number(s.id)));
  }, [slots, timeSlots, getSlotsAt]);


  const handleAddSlot = (day, startTime, endTime) => {
    if (isCellBlockedForAdd(day, startTime)) {
      toast.warning("This time slot is unavailable — another lecture occupies the class or teacher at this time.");
      return;
    }
    setEditingSlot(null);
    
    // Pre-select class/teacher/subject based on filter
    let preselectedClassId = "";
    let preselectedTeacherId = "";
    let preselectedSubjectId = "";
    
    if (filterParam && modeParam === 'class') {
      const filterId = Number(filterParam);
      const norm = (v) => String(v || "").trim().toLowerCase().replace(/\s+/g, " ");
      const normFilter = norm(filterParam);
      const targetClass = classes.find((c) => {
        const id = c.id ?? c.classId;
        const name = c.name || "";
        const section = c.section || c.classSection || "";
        const label = name ? (section ? `${name} - ${section}` : name) : "";
        if (Number.isFinite(filterId) && String(filterId) === String(filterParam).trim()) {
          return String(id) === String(filterId);
        }
        return norm(label) === normFilter || norm(name) === normFilter;
      });
      if (targetClass) {
        preselectedClassId = String(targetClass.id || targetClass.classId);
      }
    } else if (filterParam && modeParam === 'teacher') {
      const targetTeacher = teachers.find(t => 
        (t.user?.fullName || t.User?.fullName || getTeacherDisplayName(t)) === filterParam
      );
      if (targetTeacher) {
        preselectedTeacherId = String(targetTeacher.id);
      }
    } else if (filterParam && modeParam === 'subject') {
      const slotsNow = Array.isArray(timetable?.slots) ? timetable.slots : [];
      const targetSlot = slotsNow.find((s) => slotMatchesSubjectFilter(s, filterParam));
      if (targetSlot) {
        const subId = targetSlot.subjectId ?? targetSlot.subject?.id;
        if (subId != null) preselectedSubjectId = String(subId);
        const clsId = targetSlot.classId ?? targetSlot.class?.id;
        if (clsId != null) preselectedClassId = String(clsId);
      }
    }
    
    setSlotForm({
      classId: preselectedClassId,
      subjectId: preselectedSubjectId,
      teacherId: preselectedTeacherId,
      dayOfWeek: day,
      startTime: toTimeInputValue(startTime),
      endTime: toTimeInputValue(endTime || startTime),
      room: "",
      lectureType: "THEORY",
      lectureModeId: "",
      labNo: "",
      classNo: "",
      practicalBatchCode: "",
      meetingLink: "",
    });
    setConflicts([]);
    setDialogOpen(true);
  };

  const handleDeleteSlot = (slot) => {
    setDeleteConfirm(slot);
  };

  const handleEditSlot = (slot) => {
    setEditingSlot(slot);
    const classId = slot.classId ?? slot.class?.id;
    const subjectId = slot.subjectId ?? slot.subject?.id;
    const teacherId = slot.teacherId ?? slot.teacher?.id;
    setSlotForm({
      classId: normalizeSlotFk(classId),
      subjectId: normalizeSlotFk(subjectId),
      teacherId: normalizeSlotFk(teacherId),
      dayOfWeek: String(slot.dayOfWeek || "").toUpperCase(),
      startTime: toTimeInputValue(slot.startTime),
      endTime: toTimeInputValue(slot.endTime),
      room: slot.room || "",
      lectureType: String(slot.lectureType || (slot.practicalBatchCode ? "PRACTICAL" : "THEORY")).toUpperCase(),
      lectureModeId: slot.lectureModeId != null ? String(slot.lectureModeId) : "",
      labNo: slot.labNo || "",
      classNo: slot.classNo || "",
      practicalBatchCode: slot.practicalBatchCode || "",
      meetingLink: slot.meetingLink || "",
    });
    setConflicts([]);
    setDialogOpen(true);
  };

  const slotActionProps = {
    onEdit: handleEditSlot,
    onDelete: handleDeleteSlot,
    canEdit: canEditSlots,
    canDelete: canDeleteSlots,
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;
    try {
      await deleteSlotMutation.mutateAsync(deleteConfirm.id);
      toast.success("Slot deleted");
      setDeleteConfirm(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to delete");
    }
  };

  const validateBeforeSave = async () => {
    if (!slotForm.teacherId || !slotForm.classId || !slotForm.dayOfWeek || !slotForm.startTime || !slotForm.endTime) return [];
    try {
      const res = await validateSlotMutation.mutateAsync({
        classId: parseInt(slotForm.classId, 10),
        subjectId: parseInt(slotForm.subjectId, 10),
        teacherId: parseInt(slotForm.teacherId, 10),
        dayOfWeek: slotForm.dayOfWeek,
        startTime: slotForm.startTime,
        endTime: slotForm.endTime,
        excludeSlotId: editingSlot?.id,
        lectureType: slotForm.lectureType || "THEORY",
        lectureModeId: slotForm.lectureModeId ? parseInt(slotForm.lectureModeId, 10) : null,
        practicalBatchCode: slotForm.practicalBatchCode || null,
      });
      const payload = res?.data;
      return payload?.data?.conflicts || payload?.conflicts || [];
    } catch {
      return [];
    }
  };

  const isSlotFormValid = Boolean(
    slotForm.classId &&
    slotForm.subjectId &&
    slotForm.teacherId &&
    slotForm.dayOfWeek &&
    slotForm.startTime &&
    slotForm.endTime &&
    // ONLINE requires a valid meeting link
    (effectiveLectureType !== "ONLINE" || (slotForm.meetingLink && /^https?:\/\/.+/.test(slotForm.meetingLink)))
  );

  const handleSaveSlot = async () => {
    if (isSavingSlotRef.current || isSavingSlot) return;

    if (!isSlotFormValid) {
      toast.error("Please fill all required fields (Class, Subject, Teacher, Start, End)");
      return;
    }

    const breakHit = findBreakConflictForTeachingSlot(
      scheduleSlotsPerDay,
      slotForm.startTime,
      slotForm.endTime
    );
    if (breakHit) {
      const msg = `Cannot schedule during break: ${breakHit.label}`;
      toast.error(msg);
      setConflicts([{ type: "break_overlap", message: msg }]);
      return;
    }

    isSavingSlotRef.current = true;
    setIsSavingSlot(true);
    try {
      const conflictsList = await validateBeforeSave();
      setConflicts(conflictsList);
      const hasBreakOverlap = conflictsList.some((c) => c.type === "break_overlap");
      if (hasBreakOverlap) {
        toast.error(conflictsList.find((c) => c.type === "break_overlap")?.message || "Cannot schedule during a break");
        return;
      }
      if (conflictsList.length > 0) {
        toast.warning("Conflicts detected.");
      }
      if (editingSlot) {
        await updateSlotMutation.mutateAsync({
          slotId: editingSlot.id,
          entryData: {
            classId: parseInt(slotForm.classId, 10),
            subjectId: parseInt(slotForm.subjectId, 10),
            teacherId: parseInt(slotForm.teacherId, 10),
            dayOfWeek: slotForm.dayOfWeek,
            startTime: slotForm.startTime,
            endTime: slotForm.endTime,
            room: slotForm.room || null,
            lectureType: slotForm.lectureType || "THEORY",
            lectureModeId: slotForm.lectureModeId ? parseInt(slotForm.lectureModeId, 10) : null,
            labNo: slotForm.labNo || null,
            classNo: slotForm.classNo || null,
            practicalBatchCode: slotForm.practicalBatchCode || null,
            meetingLink: slotForm.meetingLink || null,
          },
        });
        toast.success("Slot updated");
      } else {
        await createSlotMutation.mutateAsync({
          classId: parseInt(slotForm.classId, 10),
          subjectId: parseInt(slotForm.subjectId, 10),
          teacherId: parseInt(slotForm.teacherId, 10),
          dayOfWeek: slotForm.dayOfWeek,
          startTime: slotForm.startTime,
          endTime: slotForm.endTime,
          room: slotForm.room || null,
          lectureType: slotForm.lectureType || "THEORY",
          lectureModeId: slotForm.lectureModeId ? parseInt(slotForm.lectureModeId, 10) : null,
          labNo: slotForm.labNo || null,
          classNo: slotForm.classNo || null,
          practicalBatchCode: slotForm.practicalBatchCode || null,
          meetingLink: slotForm.meetingLink || null,
        });
        toast.success("Slot added");
      }
      setDialogOpen(false);
      setEditingSlot(null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to save");
    } finally {
      isSavingSlotRef.current = false;
      setIsSavingSlot(false);
    }
  };

  const handleDragStart = (event) => {
    setActiveSlot(event.active.data.current);
  };

  const handleDragEnd = async (event) => {
    setActiveSlot(null);
    const { active, over } = event;
    if (!over || !active?.data?.current) return;
    const slot = active.data.current;
    const parsed = parseCellDroppableId(over.id);
    if (!parsed) return;
    const { day, startTime } = parsed;
    const timeSlot = timeSlots.find((t) => t.start === startTime);
    if (String(timeSlot?.type || "").toLowerCase() === "break") {
      toast.warning("Cannot move a slot into a break period");
      return;
    }
    const endTime = timeSlot?.end || startTime;

    if (slot.dayOfWeek === day && toTimeInputValue(slot.startTime) === startTime) return;

    const atTarget = getSlotsAt(day, startTime);
    const movingB = slot.practicalBatchCode || null;
    const cellConflict = atTarget.some((e) => {
      const eb = e.practicalBatchCode || null;
      if (!eb && !movingB) return true;
      if (eb && movingB && eb === movingB) return true;
      return false;
    });
    if (cellConflict) {
      toast.warning("Target cell already has this theory slot or same practical batch");
      return;
    }

    const breakHit = findBreakConflictForTeachingSlot(scheduleSlotsPerDay, startTime, endTime);
    if (breakHit) {
      toast.error(`Cannot schedule during break: ${breakHit.label}`);
      return;
    }

    const classId = slot.classId ?? slot.class?.id;
    const subjectId = slot.subjectId ?? slot.subject?.id;
    const teacherId = slot.teacherId ?? slot.teacher?.id;
    if (classId == null || subjectId == null || teacherId == null) {
      toast.error("Slot is missing class, subject, or teacher");
      return;
    }

    setIsUpdatingSlot(true);
    try {
      const res = await validateSlotMutation.mutateAsync({
        classId: parseInt(classId, 10),
        subjectId: parseInt(subjectId, 10),
        teacherId: parseInt(teacherId, 10),
        dayOfWeek: day,
        startTime,
        endTime,
        excludeSlotId: slot.id,
        lectureType: String(slot.lectureType || "THEORY").toUpperCase(),
        lectureModeId: slot.lectureModeId || null,
        practicalBatchCode: slot.practicalBatchCode || null,
      });
      const payload = res?.data;
      const conflicts = payload?.data?.conflicts || payload?.conflicts || [];
      const blockingConflicts = conflicts.filter((c) => !c.ignorable);
      if (blockingConflicts.length > 0) {
        const first = blockingConflicts[0];
        toast.error(first.message || "Cannot move slot (validation failed)");
        return;
      }
      if (conflicts.length > 0) {
        toast.warning("Conflicts detected (allowed by config).");
      }

      await updateSlotMutation.mutateAsync({
        slotId: slot.id,
        entryData: {
          dayOfWeek: day,
          startTime,
          endTime,
        },
      });
      toast.success("Slot moved");
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || "Failed to move");
    } finally {
      setIsUpdatingSlot(false);
    }
  };

  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <CircularProgress size={48} />
        <Typography variant="body2" color="text.secondary">
          Loading timetable data...
        </Typography>
      </Box>
    );
  }

  if (error || !timetable) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Failed to load timetable
      </Alert>
    );
  }

  if (timetable.status === "locked") {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        This timetable is locked and cannot be edited. Mark it as <strong>Draft</strong> from the
        Timetable list menu, or use Regenerate to create a new version.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Global loading overlay for drag and drop operations */}
      {isUpdatingSlot && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              bgcolor: "background.paper",
              p: 3,
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <CircularProgress size={48} />
            <Typography variant="h6" color="text.secondary">
              Updating slot...
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Please wait while we move the slot to its new position
            </Typography>
          </Box>
        </Box>
      )}

      {isFetching && !isLoading && (
        <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />
      )}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <IconButton onClick={() => navigate(`${rolePath}/timetable/view/${id}`)}>
            <ArrowBack />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              Edit Timetable
              {filterParam && modeParam && (
                <Typography component="span" variant="h6" fontWeight={400} color="primary" sx={{ ml: 1 }}>
                  - {filterParam}
                </Typography>
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {timetable.name} • {timetable.academicYear}
              {" • "}
              {timetable.department?.name || "Institute-level"}
              {filterParam && modeParam && (
                <Typography component="span" sx={{ ml: 1 }}>
                  • Editing {kanbanModeLabel(modeParam)} timetable only
                </Typography>
              )}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {canEditTimetableDetails && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={openEditDetails}
            >
              Edit Details
            </Button>
          )}
          {filterParam && modeParam && (
            <Button
              variant="outlined"
              onClick={() => navigate(`${rolePath}/timetable/edit/${id}`)}
            >
              Edit All Timetables
            </Button>
          )}
        </Box>
      </Box>

      {/* Edit timetable details (name + owning department) */}
      <Dialog
        open={detailsDialogOpen}
        onClose={(e, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          setDetailsDialogOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Timetable Details</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Timetable Name"
                value={detailsForm.name}
                onChange={(e) =>
                  setDetailsForm((prev) => ({ ...prev, name: e.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth disabled={departmentsLoading}>
                <InputLabel>Department</InputLabel>
                <Select
                  label="Department"
                  value={detailsForm.departmentId}
                  onChange={(e) =>
                    setDetailsForm((prev) => ({ ...prev, departmentId: e.target.value }))
                  }
                >
                  <MenuItem value="">
                    <em>Institute-level (no department)</em>
                  </MenuItem>
                  {departments.map((d) => (
                    <MenuItem
                      key={d.id ?? d.departmentId}
                      value={String(d.id ?? d.departmentId)}
                    >
                      {d.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>
                  {departmentsLoading
                    ? "Loading departments..."
                    : "Moves this timetable to the selected department. Leave as institute-level for classes with no department."}
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDetailsDialogOpen(false)}
            disabled={updateTimetableMetaMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveDetails}
            disabled={updateTimetableMetaMutation.isPending}
            startIcon={
              updateTimetableMetaMutation.isPending ? (
                <CircularProgress size={18} color="inherit" />
              ) : null
            }
          >
            {updateTimetableMetaMutation.isPending ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {unplacedSlots.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {unplacedSlots.length} lecture slot(s) are saved but not shown on the grid (unusual start times or outside school hours).{" "}
          {unplacedSlots.slice(0, 5).map((s) => (
            <Typography key={s.id} component="span" variant="body2" display="block" sx={{ mt: 0.5 }}>
              {s.dayOfWeek} {toTimeInputValue(s.startTime)}–{toTimeInputValue(s.endTime)}
              {s.subject?.name ? ` — ${s.subject.name}` : ""}
            </Typography>
          ))}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={timetableKanbanCollisionDetection}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={() => setActiveSlot(null)}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            maxHeight: "calc(100vh - 220px)",
            overflow: "auto",
          }}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: "fixed",
              width: "100%",
              minWidth: 880,
              "& .MuiTableCell-root": { borderColor: "divider" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    width: 118,
                    minWidth: 118,
                    bgcolor: "grey.100",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    borderBottom: "2px solid",
                    borderColor: "divider",
                  }}
                >
                  Time
                </TableCell>
                {DAYS.map((day) => (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      fontWeight: 700,
                      fontSize: "0.75rem",
                      borderBottom: "2px solid",
                      borderColor: "primary.dark",
                      py: 1.25,
                    }}
                  >
                    {day.slice(0, 3)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {timeSlots.map((ts, idx) => {
                const isBreak = String(ts.type || "").toLowerCase() === "break";
                const rowKey = `${ts.start}-${ts.end}-${idx}`;
                return (
                  <TableRow
                    key={rowKey}
                    sx={{
                      ...(isBreak && {
                        bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
                        "& .MuiTableCell-root": {
                          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.06),
                        },
                      }),
                    }}
                  >
                    <TableCell
                      sx={{
                        verticalAlign: "top",
                        bgcolor: (theme) =>
                          isBreak ? alpha(theme.palette.warning.light, 0.45) : theme.palette.grey[50],
                        borderLeft: isBreak ? 4 : 0,
                        borderLeftColor: "warning.main",
                        py: 1.25,
                        pr: 1,
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} display="block" sx={{ lineHeight: 1.3 }}>
                        {ts.start}–{ts.end}
                      </Typography>
                      {isBreak && (
                        <Chip
                          size="small"
                          label={ts.label || "Break"}
                          color="warning"
                          variant="outlined"
                          sx={{ mt: 0.75, height: 22, fontSize: "0.65rem", fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    {DAYS.map((day) => {
                      const slotsAt = getSlotsAt(day, ts.start);
                      const cellId = `${day}_${ts.start}`;
                      if (isBreak) {
                        return (
                          <TableCell
                            key={cellId}
                            align="center"
                            sx={{
                              verticalAlign: "middle",
                              p: 1,
                              borderLeft: "1px solid",
                              borderLeftColor: "divider",
                            }}
                          >
                            <Box
                              sx={{
                                minHeight: 96,
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 0.5,
                                py: 1,
                                borderRadius: 1,
                                border: "1px solid",
                                borderColor: (theme) => alpha(theme.palette.warning.dark, 0.25),
                                bgcolor: (theme) => alpha(theme.palette.warning.light, 0.35),
                              }}
                            >
                              {slotsAt.length > 0 ? (
                                <Stack spacing={1} sx={{ width: "100%" }}>
                                  {slotsAt.map((s) => (
                                    <SlotCard
                                      key={s.id}
                                      slot={s}
                                      {...slotActionProps}
                                      isDragging={false}
                                      showBreakWarning={slotOverlapsConfiguredBreak(s, scheduleSlotsPerDay)}
                                    />
                                  ))}
                                </Stack>
                              ) : (
                                <>
                                  <Typography variant="caption" fontWeight={700} color="warning.dark" display="block">
                                    {ts.label || "Break"}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, fontSize: "0.65rem" }}>
                                    No teaching
                                  </Typography>
                                </>
                              )}
                            </Box>
                          </TableCell>
                        );
                      }
                      return (
                        <TableCell
                          key={cellId}
                          sx={{
                            verticalAlign: "top",
                            p: 1,
                            borderLeft: "1px solid",
                            borderLeftColor: "divider",
                            bgcolor: "background.paper",
                          }}
                        >
                          <DroppableCell id={cellId}>
                            <Stack spacing={1} sx={{ width: "100%" }}>
                              {slotsAt.map((s) => (
                                <SlotCard
                                  key={s.id}
                                  slot={s}
                                  {...slotActionProps}
                                  isDragging={false}
                                  showBreakWarning={slotOverlapsConfiguredBreak(s, scheduleSlotsPerDay)}
                                />
                              ))}
                              {canEditSlots ? (
                              slotsAt.length === 0 && isCellCoveredByOwnSpanningSlot(day, ts.start) ? (
                                // Continuation of this class's own multi-period lecture from an
                                // earlier row (e.g. a 09:00–11:00 slot covering the 10:00–11:00
                                // cell). Not a conflict — show as occupied continuation.
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    minHeight: 88,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontStyle: "italic",
                                    opacity: 0.7,
                                  }}
                                >
                                  ↑ continued
                                </Typography>
                              ) : slotsAt.length === 0 && isCellBlockedForAdd(day, ts.start) ? (
                                <Typography
                                  variant="caption"
                                  color="error.main"
                                  sx={{
                                    minHeight: 88,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 600,
                                  }}
                                >
                                  Unavailable
                                </Typography>
                              ) : (
                              <Button
                                fullWidth
                                variant="outlined"
                                size="small"
                                startIcon={<Add />}
                                onClick={() => handleAddSlot(day, ts.start, ts.end)}
                                sx={{ minHeight: slotsAt.length ? 40 : 88, flex: 1 }}
                              >
                                {slotsAt.length ? "Add line" : "Add"}
                              </Button>
                              )
                              ) : null}
                            </Stack>
                          </DroppableCell>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <DragOverlay>
          {activeSlot ? (
            <Box sx={{ maxWidth: 200 }}>
              <SlotCard
                slot={activeSlot}
                {...slotActionProps}
                isDragging
                showBreakWarning={slotOverlapsConfiguredBreak(activeSlot, scheduleSlotsPerDay)}
              />
            </Box>
          ) : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={dialogOpen} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setDialogOpen(false); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingSlot ? "Edit Slot" : "Add Slot"}
          {filterParam && modeParam && (
            <Typography variant="caption" display="block" color="primary" sx={{ mt: 0.5 }}>
              For {kanbanModeLabel(modeParam)}: {filterParam}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {dialogBreakConflict && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Cannot schedule during break: {dialogBreakConflict.label}. Change start/end to a lecture period.
            </Alert>
          )}
          {conflicts.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {conflicts.map((c, i) => (
                <Typography key={i} variant="body2">{c.message}</Typography>
              ))}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Class</InputLabel>
                <Select
                  value={slotForm.classId || ""}
                  label="Class"
                  onChange={(e) =>
                    setSlotForm({
                      ...slotForm,
                      classId: e.target.value ? String(e.target.value) : "",
                      subjectId: "",
                      teacherId: "",
                      practicalBatchCode: "",
                    })
                  }
                  disabled={filterParam && modeParam === 'class'}
                >
                  <MenuItem value="">
                    <em>Select a class</em>
                  </MenuItem>
                  {classesForSelect.map((c) => {
                    const classId = c.id ?? c.classId;
                    return (
                      <MenuItem key={String(classId)} value={String(classId)}>
                        {formatClassLabel(c)}
                      </MenuItem>
                    );
                  })}
                </Select>
                {filterParam && modeParam === 'class' && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, ml: 1.75 }}>
                    Editing {filterParam} timetable only
                  </Typography>
                )}
                {filterParam && modeParam === 'subject' && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, ml: 1.75 }}>
                    Class is required — pick the class for this subject slot
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Subject</InputLabel>
                <Select
                  value={slotForm.subjectId || ""}
                  label="Subject"
                  onChange={(e) =>
                    setSlotForm({
                      ...slotForm,
                      subjectId: e.target.value ? String(e.target.value) : "",
                      teacherId: "",
                    })
                  }
                  disabled={
                    (filterParam && modeParam === 'subject') ||
                    !slotForm.classId ||
                    subjectsLoading
                  }
                >
                  <MenuItem value="">
                    <em>{!slotForm.classId ? "Select class first" : subjectsLoading ? "Loading..." : "Select a subject"}</em>
                  </MenuItem>
                  {subjectsForSelect.map((s) => {
                    const subjectId = s.id ?? s.subjectId;
                    const subjectName = s.name ?? s.subjectName ?? `Subject ${subjectId}`;
                    return (
                      <MenuItem key={String(subjectId)} value={String(subjectId)}>
                        {subjectName}
                      </MenuItem>
                    );
                  })}
                </Select>
                {filterParam && modeParam === 'subject' && (
                  <Typography variant="caption" color="primary" sx={{ mt: 0.5, ml: 1.75 }}>
                    Editing {filterParam} timetable only
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth required error={!slotForm.teacherId && (slotForm.classId || slotForm.subjectId)}>
                <InputLabel>Teacher *</InputLabel>
                <Select
                  value={slotForm.teacherId || ""}
                  label="Teacher *"
                  onChange={(e) =>
                    setSlotForm({
                      ...slotForm,
                      teacherId: e.target.value ? String(e.target.value) : "",
                    })
                  }
                  disabled={!slotForm.classId || !slotForm.subjectId || teachersLoading}
                >
                  <MenuItem value="">
                    <em>
                      {!slotForm.classId || !slotForm.subjectId
                        ? "Select class and subject first"
                        : teachersLoading
                          ? "Loading..."
                          : teachersForSelect.length === 0
                            ? "No teachers assigned to this class and subject"
                            : "Select a teacher"}
                    </em>
                  </MenuItem>
                  {teachersForSelect.map((t) => (
                    <MenuItem key={String(t.id)} value={String(t.id)}>
                      {getTeacherDisplayName(t) || `Teacher ${t.id}`}
                    </MenuItem>
                  ))}
                </Select>
                {!slotForm.teacherId && (slotForm.classId || slotForm.subjectId) && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    Teacher is required
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Start"
                type="time"
                value={slotForm.startTime}
                onChange={(e) => {
                  setSlotForm({ ...slotForm, startTime: e.target.value });
                  setConflicts([]);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="End"
                type="time"
                value={slotForm.endTime}
                onChange={(e) => {
                  setSlotForm({ ...slotForm, endTime: e.target.value });
                  setConflicts([]);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                fullWidth
                helperText="Use 00:00 for midnight end (e.g. 23:00–00:00)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Room" value={slotForm.room} onChange={(e) => setSlotForm({ ...slotForm, room: e.target.value })} fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Lecture Type</InputLabel>
                <Select
                  value={slotForm.lectureType || "THEORY"}
                  label="Lecture Type"
                  onChange={(e) =>
                    setSlotForm({
                      ...slotForm,
                      lectureType: e.target.value ? String(e.target.value).toUpperCase() : "THEORY",
                    })
                  }
                >
                  <MenuItem value="THEORY">Theory</MenuItem>
                  <MenuItem value="PRACTICAL">Practical</MenuItem>
                  <MenuItem value="LAB">Lab</MenuItem>
                  <MenuItem value="TUTORIAL">Tutorial</MenuItem>
                  <MenuItem value="PROJECT">Project</MenuItem>
                  <MenuItem value="INTERNSHIP">Internship</MenuItem>
                  <MenuItem value="ONLINE">Online</MenuItem>
                </Select>
                <FormHelperText>Theory/Online is full class; non-theory (except Online) requires batch</FormHelperText>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={lectureModesError} disabled={lectureModesLoading}>
                <InputLabel id="slot-lecture-mode-label" shrink>Lecture mode</InputLabel>
                <Select
                  labelId="slot-lecture-mode-label"
                  value={slotForm.lectureModeId || ""}
                  label="Lecture mode"
                  displayEmpty
                  onChange={(e) => {
                    const nextModeId = e.target.value ? String(e.target.value) : "";
                    const nextMode = lectureModesForSelect.find((m) => String(m.id) === String(nextModeId));
                    const inferred = inferLectureTypeFromMode(nextMode);
                    setSlotForm({
                      ...slotForm,
                      lectureModeId: nextModeId,
                      lectureType: inferred || slotForm.lectureType || "THEORY",
                    });
                  }}
                  renderValue={(val) => {
                    if (!val) return "Theory (not specified)";
                    const m = lectureModesForSelect.find((x) => String(x.id) === String(val));
                    return m ? `${m.code} — ${m.name}` : `Mode #${val}`;
                  }}
                >
                  <MenuItem value="">
                    <em>None (treat as theory)</em>
                  </MenuItem>
                  {lectureModesForSelect.map((m) => (
                    <MenuItem key={m.id} value={String(m.id)}>
                      {m.code} — {m.name}
                    </MenuItem>
                  ))}
                </Select>
                {lectureModesLoading ? (
                  <FormHelperText>Loading lecture modes…</FormHelperText>
                ) : lectureModesError ? (
                  <FormHelperText error>
                    {lectureModesErr?.response?.data?.message || lectureModesErr?.message || "Could not load modes"}
                  </FormHelperText>
                ) : (
                  <FormHelperText>Theory vs practical — from your institute settings</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              {classPracticalBatches.length > 0 ? (
                <FormControl fullWidth>
                  <InputLabel>Batch</InputLabel>
                  <Select
                    value={slotForm.practicalBatchCode || ""}
                    label="Batch"
                    disabled={!isBatchBasedLectureType}
                    onChange={(e) => setSlotForm({ ...slotForm, practicalBatchCode: e.target.value ? String(e.target.value).toUpperCase() : "" })}
                  >
                    <MenuItem value="">
                      <em>None (theory/full class)</em>
                    </MenuItem>
                    {classPracticalBatches.map((b) => (
                      <MenuItem key={b} value={b}>{b}</MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Configured from class practical batches</FormHelperText>
                </FormControl>
              ) : (
                <TextField
                  label="Batch"
                  value=""
                  onChange={() => {}}
                  disabled
                  fullWidth
                  helperText="No practical batches are configured for this class. Practical sessions will run as full-class sessions."
                />
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Lab number"
                value={slotForm.labNo}
                onChange={(e) => setSlotForm({ ...slotForm, labNo: e.target.value })}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Classroom no."
                value={slotForm.classNo}
                onChange={(e) => setSlotForm({ ...slotForm, classNo: e.target.value })}
                fullWidth
              />
            </Grid>
            {effectiveLectureType === "ONLINE" && (
              <Grid item xs={12}>
                <TextField
                  label="Meeting Link *"
                  value={slotForm.meetingLink || ""}
                  onChange={(e) => setSlotForm({ ...slotForm, meetingLink: e.target.value })}
                  fullWidth
                  placeholder="https://meet.google.com/... or https://zoom.us/j/..."
                  error={Boolean(slotForm.meetingLink && !/^https?:\/\/.+/.test(slotForm.meetingLink))}
                  helperText={
                    slotForm.meetingLink && !/^https?:\/\/.+/.test(slotForm.meetingLink)
                      ? "Enter a valid URL starting with http:// or https://"
                      : "Required for Online sessions — students will see this link in their timetable"
                  }
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isSavingSlot}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            onClick={handleSaveSlot}
            disabled={
              !isSlotFormValid ||
              Boolean(dialogBreakConflict) ||
              isSavingSlot
            }
            startIcon={isSavingSlot ? <CircularProgress size={18} color="inherit" /> : null}
          >
            {isSavingSlot ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleteConfirm} onClose={(e, reason) => { if (reason === "backdropClick" || reason === "escapeKeyDown") return; setDeleteConfirm(null); }}>
        <DialogTitle>Delete Slot?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this slot?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteConfirm} disabled={deleteSlotMutation.isPending}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimetableKanbanEditor;
